import React, { useState, useEffect, useCallback } from 'react';
import {
    Dropdown,
    Option,
    Field,
    Spinner,
    Text
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { getRecordTypes, RecordTypeResult, CaseResult, InsertableArchiveResult } from '../../../services/api/dataServiceOutlookQuickSave';
import RequiredLabel from './RequiredLabel';

interface RecordTypeDropdownProps {
    selectedRecordType: RecordTypeResult | null;
    selectedCase: CaseResult | null;
    selectedArchive: InsertableArchiveResult | null;
    onRecordTypeChange: (recordType: RecordTypeResult | null) => void;
}

const RecordTypeDropdown: React.FC<RecordTypeDropdownProps> = ({
    selectedRecordType,
    selectedCase,
    selectedArchive,
    onRecordTypeChange
}) => {
    const { t } = useTranslation();
    const [recordTypes, setRecordTypes] = useState<RecordTypeResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>('');

    const loadRecordTypes = useCallback(async (caseRecno: number, archiveRecno: number) => {
        setIsLoading(true);
        setError('');
        setRecordTypes([]);

        try {
            const result = await getRecordTypes(caseRecno, archiveRecno);
            setRecordTypes(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load record types');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedCase && selectedArchive) {
            void loadRecordTypes(selectedCase.Recno, selectedArchive.Recno);
        } else {
            setRecordTypes([]);
            setError('');
        }
    }, [selectedCase, selectedArchive, loadRecordTypes]);

    const handleOptionSelect = (_event: unknown, data: { optionValue?: string }) => {
        const selectedRecordTypeRecno = data.optionValue;
        if (selectedRecordTypeRecno) {
            const recordType = recordTypes.find(rt => rt.Recno.toString() === selectedRecordTypeRecno);
            onRecordTypeChange(recordType || null);
        } else {
            onRecordTypeChange(null);
        }
    };

    const isDisabled = !selectedCase || !selectedArchive;

    return (
        <Field
            label={<RequiredLabel text={t("Record Type")} required={true} />}
            validationMessage={error || undefined}
            validationState={error ? "error" : "none"}
        >
            {isLoading ? (
                <div>
                    <Spinner size="small" />
                    <Text>{t("Loading...")}</Text>
                </div>
            ) : (
                <Dropdown
                    placeholder={t("Select a record type")}
                    value={selectedRecordType?.Description || ''}
                    selectedOptions={selectedRecordType ? [selectedRecordType.Recno.toString()] : []}
                    onOptionSelect={handleOptionSelect}
                    disabled={isDisabled}
                >
                    {recordTypes.map((recordType) => (
                        <Option
                            key={recordType.Recno}
                            value={recordType.Recno.toString()}
                            text={recordType.Description}
                        >
                            {recordType.Description}
                        </Option>
                    ))}
                </Dropdown>
            )}
        </Field>
    );
};

export default RecordTypeDropdown;
